import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";

import UiSpinners from "pages/Components/UIElement/UiSpinners";
import useBookings from "hooks/useBookings";
import { BookingCard } from "Common/Components/BookingCard";
import { useMutation } from "react-query";
import { CreateBookingInput } from "types/booking.interface";
import { createBooking } from "services/booking.service";
import { toast, ToastContainer } from "react-toastify";
import { Plus } from "lucide-react";
import Modal from "Common/Components/Modal";
import BookingForm from "./BookingForm";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";

const Bookings = () => {
  const { data, isLoading, hasNextPage, handleRefresh, handleEndReached } =
    useBookings();
  const [showAdd, setShowAdd] = useState(false);

  // Followed here from a booking alert — an unanswered booking is the platform's
  // most common stall, so landing on the right card matters.
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    data,
    isLoading
  );
  const { isLoading: creating, mutate } = useMutation(
    async (input: CreateBookingInput) => await createBooking(input),
    {
      onSuccess(data) {
        toast.success(data?.message);
        handleRefresh();
        toggleAdd();
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message);
      },
    }
  );

  const toggleAdd = () => {
    setShowAdd(!showAdd);
  };

  const handleSubmit = (data: CreateBookingInput) => {
    mutate(data);
  };
  return (
    <React.Fragment>
      <BreadCrumb title="Bookings" pageTitle="Bookings" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="lg:col-span-3 lg:col-start-10 my-4">
        <div className="flex gap-2 lg:justify-end">
          <button
            onClick={toggleAdd}
            type="button"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            <Plus className="inline-block size-4" />{" "}
            <span className="align-middle">Create Booking</span>
          </button>
        </div>
      </div>

      {isLoading ? (
        <UiSpinners />
      ) : (
        <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
          <div className="xl:col-span-12">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" id="usersTable">
              <div className="overflow-x-auto">
                {data && data?.length! > 0 ? (
                  <div className=" flex flex-col lg:flex-row justify-stretch items-center gap-4 flex-wrap">
                    {data?.map((data) => (
                      <div
                        key={data._id}
                        ref={scrollRowIntoView(isFocused(data._id))}
                        className={`w-full lg:w-[48%] self-stretch rounded-lg ${focusRowClass(
                          isFocused(data._id)
                        )}`}
                      >
                        <BookingCard booking={data} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="py-10 text-center text-sm text-gray-500">No bookings found</p>
                )}

                <div className="flex gap-4 items-center justify-end my-4 px-4">
                  {hasNextPage && (
                    <button
                      className="text-sm text-indigo-600 hover:underline"
                      onClick={() => handleEndReached()}
                    >
                      Load more
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Modal
            show={showAdd}
            onHide={toggleAdd}
            id="defaultModal"
            modal-center="true"
                size="md"
          >
            <Modal.Header
              className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
              closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
            >
              <Modal.Title className="text-16">Create Booking</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 overflow-y-auto">
              <BookingForm
                onCancel={toggleAdd}
                onSubmit={handleSubmit}
                isLoading={creating}
              />
            </Modal.Body>
          </Modal>
        </div>
      )}
    </React.Fragment>
  );
};

export default Bookings;
